import { createFileRoute, useParams, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { ArrowLeft, Briefcase, ShieldCheck, ShieldX, Power, FileText, DollarSign, Send } from 'lucide-react'
import {
  useWorkerAdminDetail,
  useSetWorkerActive,
  useReviewWorkerVerification,
  useWorkerPayouts,
  useCreatePayout,
  useReleasePayout,
  useWorkerJobs,
} from '@/hooks/data/useWorkers'
import { useAlertContext } from '@/providers/AlertProvider'
import { API_BASE_URL } from '@/api/config'
import { tokenStore } from '@/api/tokenStore'

const nzd = new Intl.NumberFormat('en-NZ', { style: 'currency', currency: 'NZD' })
const dateFmt = new Intl.DateTimeFormat('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' })

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10)
}

function statusColor(s: string) {
  return (
    {
      NotSubmitted: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
      Pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
      Verified: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      Rejected: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    }[s] ?? 'bg-gray-100 text-gray-600'
  )
}

function WorkerDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string }
  const navigate = useNavigate()
  const { data: worker, isLoading, isError, error } = useWorkerAdminDetail(id)
  const setActive = useSetWorkerActive()
  const review = useReviewWorkerVerification()
  const { data: payouts } = useWorkerPayouts(id)
  const createPayout = useCreatePayout()
  const releasePayout = useReleasePayout(id)
  const { success: showSuccess, error: showError, confirm } = useAlertContext()
  const [rejectionReason, setRejectionReason] = useState('')
  const [showRejectForm, setShowRejectForm] = useState(false)
  const [jobsTab, setJobsTab] = useState<'upcoming' | 'completed'>('upcoming')
  const { data: jobsPage, isLoading: jobsLoading } = useWorkerJobs(id, jobsTab)
  const [viewingDocument, setViewingDocument] = useState(false)
  const [periodStart, setPeriodStart] = useState(() => isoDate(new Date(Date.now() - 6 * 86400000)))
  const [periodEnd, setPeriodEnd] = useState(() => isoDate(new Date()))

  // A plain <a href> can't carry the Bearer token the gateway requires (this route isn't
  // cookie-authenticated) — fetch the bytes with the token attached, then open a blob URL.
  const viewDocument = async () => {
    if (!worker) return
    setViewingDocument(true)
    try {
      const token = tokenStore.get()?.accessToken
      const response = await fetch(`${API_BASE_URL}/api/v1/workers/${worker.id}/verification/document`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      })
      if (!response.ok) throw new Error(`Could not load the document (${response.status})`)
      const blob = await response.blob()
      window.open(URL.createObjectURL(blob), '_blank', 'noopener,noreferrer')
    } catch (err) {
      showError('Failed', (err as Error)?.message || 'Could not load the document.')
    } finally {
      setViewingDocument(false)
    }
  }

  const toggleActive = () => {
    if (!worker) return
    const next = !worker.active
    confirm({
      title: next ? 'Reactivate worker' : 'Deactivate worker',
      message: next
        ? 'This worker will be visible again for assignment and public search.'
        : "This worker will drop out of assignment tooling and public search. Their history is kept — nothing is deleted.",
      type: 'warning',
      confirmText: next ? 'Reactivate' : 'Deactivate',
      cancelText: 'Cancel',
      showCancel: true,
      onConfirm: async () => {
        try {
          await setActive.mutateAsync({ workerId: worker.id, active: next })
          showSuccess(next ? 'Reactivated' : 'Deactivated', `${worker.name} is now ${next ? 'active' : 'inactive'}.`)
        } catch (err) {
          showError('Failed', (err as Error)?.message || 'Could not update this worker.')
        }
      },
    })
  }

  const approve = async () => {
    if (!worker) return
    try {
      await review.mutateAsync({ workerId: worker.id, approve: true })
      showSuccess('Verified', `${worker.name}'s ID has been verified.`)
    } catch (err) {
      showError('Failed', (err as Error)?.message || 'Could not verify this worker.')
    }
  }

  const reject = async () => {
    if (!worker || !rejectionReason.trim()) return
    try {
      await review.mutateAsync({ workerId: worker.id, approve: false, rejectionReason: rejectionReason.trim() })
      showSuccess('Rejected', `${worker.name}'s submission was rejected.`)
      setShowRejectForm(false)
      setRejectionReason('')
    } catch (err) {
      showError('Failed', (err as Error)?.message || 'Could not reject this worker.')
    }
  }

  const generatePayout = async () => {
    if (!worker) return
    try {
      const result = await createPayout.mutateAsync({
        workerId: worker.id,
        periodStart: new Date(periodStart).toISOString(),
        periodEnd: new Date(periodEnd).toISOString(),
      })
      showSuccess('Payout created', `${nzd.format(result.amount)} for ${result.hoursWorked.toFixed(1)}h.`)
    } catch (err) {
      showError('Failed', (err as Error)?.message || 'Could not create a payout for this period.')
    }
  }

  const release = (payoutId: string, amount: number) => {
    confirm({
      title: 'Release payment',
      message: `Mark ${nzd.format(amount)} as paid? The worker will be notified.`,
      type: 'warning',
      confirmText: 'Release',
      cancelText: 'Cancel',
      showCancel: true,
      onConfirm: async () => {
        try {
          await releasePayout.mutateAsync(payoutId)
          showSuccess('Released', 'Payment marked as paid.')
        } catch (err) {
          showError('Failed', (err as Error)?.message || 'Could not release this payment.')
        }
      },
    })
  }

  if (isLoading) return <p className="p-4 text-sm text-gray-500">Loading…</p>
  if (isError || !worker) return <p className="p-4 text-sm text-red-600">Failed to load: {(error as Error)?.message ?? 'not found'}.</p>

  return (
    <div className="p-2 md:p-4 space-y-4">
      <button
        onClick={() => navigate({ to: '/workers' })}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
      >
        <ArrowLeft className="h-4 w-4" /> Back to workers
      </button>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">{worker.name}</h1>
            <p className="text-sm text-gray-500">{worker.email ?? '—'}</p>
            <p className="mt-1 text-sm text-gray-500">
              {worker.hourlyRate != null ? `$${worker.hourlyRate}/hr` : 'No rate set'} · Max {worker.maxDailyJobs} jobs/day
            </p>
          </div>
          <button
            onClick={toggleActive}
            disabled={setActive.isPending}
            className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50 ${
              worker.active
                ? 'border border-red-300 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            <Power className="h-4 w-4" /> {worker.active ? 'Deactivate' : 'Reactivate'}
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4 border-t border-gray-100 pt-4 dark:border-gray-700 sm:grid-cols-4">
          <div>
            <p className="text-xs text-gray-500">Upcoming jobs</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">{worker.upcomingJobsCount}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Completed jobs</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">{worker.completedJobsCount}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Total earnings</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">{nzd.format(worker.totalEarnings)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Service areas</p>
            <p className="text-sm text-gray-700 dark:text-gray-300">{worker.serviceAreaCodes.join(', ') || '—'}</p>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">ID Verification</h2>
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColor(worker.verificationStatus)}`}>
            {worker.verificationStatus}
          </span>
        </div>

        {worker.verificationSubmittedAt && (
          <p className="text-sm text-gray-500">Submitted {dateFmt.format(new Date(worker.verificationSubmittedAt))}</p>
        )}
        {worker.verificationRejectionReason && (
          <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
            Previous rejection reason: {worker.verificationRejectionReason}
          </p>
        )}

        {worker.verificationStatus === 'Pending' && (
          <div className="space-y-3 pt-2">
            <button
              onClick={viewDocument}
              disabled={viewingDocument}
              className="flex items-center gap-2 text-sm text-blue-600 hover:underline disabled:opacity-50 dark:text-blue-400"
            >
              <FileText className="h-4 w-4" /> {viewingDocument ? 'Loading…' : 'View submitted document'}
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={approve}
                disabled={review.isPending}
                className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm text-white hover:bg-green-700 disabled:opacity-50"
              >
                <ShieldCheck className="h-4 w-4" /> Verify
              </button>
              <button
                onClick={() => setShowRejectForm((v) => !v)}
                className="flex items-center gap-2 rounded-lg border border-red-300 px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20"
              >
                <ShieldX className="h-4 w-4" /> Reject
              </button>
            </div>
            {showRejectForm && (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Reason for rejection"
                  className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                <button
                  onClick={reject}
                  disabled={!rejectionReason.trim() || review.isPending}
                  className="rounded-lg bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700 disabled:opacity-50"
                >
                  Confirm reject
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
            <Briefcase className="h-5 w-5" /> Job History
          </h2>
          <div className="flex gap-1 rounded-lg bg-gray-100 p-1 text-sm dark:bg-gray-900/40">
            {(['upcoming', 'completed'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setJobsTab(tab)}
                className={`rounded-md px-3 py-1 capitalize ${
                  jobsTab === tab ? 'bg-white text-gray-900 shadow dark:bg-gray-700 dark:text-white' : 'text-gray-500'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {jobsLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : !jobsPage || jobsPage.jobs.length === 0 ? (
          <p className="text-sm text-gray-500">No {jobsTab} jobs.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
              <tr>
                <th className="py-2">Service</th>
                <th className="py-2">Suburb</th>
                <th className="py-2">Scheduled</th>
                <th className="py-2">Price</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {jobsPage.jobs.map((j) => (
                <tr key={j.jobId}>
                  <td className="py-2">{j.serviceType}</td>
                  <td className="py-2">{j.suburb}</td>
                  <td className="py-2">{dateFmt.format(new Date(j.scheduledStart))}</td>
                  <td className="py-2 font-medium">{nzd.format(j.agreedPrice)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 space-y-3">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
          <DollarSign className="h-5 w-5" /> Payouts
        </h2>

        <div className="flex flex-wrap items-end gap-2">
          <label className="text-sm text-gray-600 dark:text-gray-400">
            From
            <input
              type="date"
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
              className="ml-2 rounded-lg border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            />
          </label>
          <label className="text-sm text-gray-600 dark:text-gray-400">
            To
            <input
              type="date"
              value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
              className="ml-2 rounded-lg border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            />
          </label>
          <button
            onClick={generatePayout}
            disabled={createPayout.isPending || worker.hourlyRate == null}
            title={worker.hourlyRate == null ? 'This worker has no hourly rate set' : undefined}
            className="rounded-lg bg-blue-600 px-4 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {createPayout.isPending ? 'Calculating…' : 'Generate payout'}
          </button>
        </div>
        {worker.hourlyRate == null && (
          <p className="text-xs text-amber-600 dark:text-amber-400">
            This worker hasn&apos;t set an hourly rate on their profile — nothing to calculate yet.
          </p>
        )}

        {!payouts || payouts.length === 0 ? (
          <p className="text-sm text-gray-500">No payouts yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
              <tr>
                <th className="py-2">Period</th>
                <th className="py-2">Hours</th>
                <th className="py-2">Amount</th>
                <th className="py-2">Status</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {payouts.map((p) => (
                <tr key={p.id}>
                  <td className="py-2">
                    {dateFmt.format(new Date(p.periodStart))} – {dateFmt.format(new Date(p.periodEnd))}
                  </td>
                  <td className="py-2">{p.hoursWorked.toFixed(1)}h</td>
                  <td className="py-2 font-medium">{nzd.format(p.amount)}</td>
                  <td className="py-2">
                    <span
                      className={`rounded px-2 py-0.5 text-xs font-medium ${
                        p.status === 'Paid'
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                      }`}
                    >
                      {p.status}
                    </span>
                  </td>
                  <td className="py-2 text-right">
                    {p.status === 'Pending' && (
                      <button
                        onClick={() => release(p.id, p.amount)}
                        disabled={releasePayout.isPending}
                        className="flex items-center gap-1 text-blue-600 hover:underline disabled:opacity-50 dark:text-blue-400"
                      >
                        <Send className="h-3.5 w-3.5" /> Release
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

export const Route = createFileRoute('/(admin)/workers/$id/')({ component: WorkerDetailPage })
